(() => {
  const INCREASE_PX = 1.5;
  const STEP_SELECTOR = '.bcu-service-page .bcu-service-step';
  const MARKER = 'data-bcu-step-font-increased';
  const originalInlineStyles = new WeakMap();
  const adjustedElements = new Set();
  let frameId = 0;
  let recalibrateOnNextFrame = false;

  const hasDirectText = (element) => Array.from(element.childNodes).some((node) => (
    node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== ''
  ));

  const isTextControl = (element) => {
    if (element.matches('select, textarea')) return true;
    if (!element.matches('input')) return false;

    return !['checkbox', 'radio', 'hidden', 'range', 'color', 'file'].includes(
      (element.getAttribute('type') || 'text').toLowerCase()
    );
  };

  const isCustomerFacingText = (element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (!element.closest(STEP_SELECTOR)) return false;
    if (element.hasAttribute(MARKER)) return false;
    if (element.matches('script, style, template, svg, path')) return false;
    if (element.closest('[hidden], [aria-hidden="true"], .bcu-visually-hidden, .visually-hidden')) return false;
    if (!hasDirectText(element) && !isTextControl(element)) return false;
    if (element.getClientRects().length === 0) return false;

    const style = window.getComputedStyle(element);
    const size = Number.parseFloat(style.fontSize);
    return style.display !== 'none' && style.visibility !== 'hidden' && Number.isFinite(size) && size > 0;
  };

  const formatPixels = (value) => `${Number(value.toFixed(2))}px`;

  const applyIncrease = () => {
    const elements = Array.from(document.querySelectorAll(`${STEP_SELECTOR} *`))
      .filter(isCustomerFacingText);

    const measurements = elements.map((element) => ({
      element,
      size: Number.parseFloat(window.getComputedStyle(element).fontSize)
    }));

    measurements.forEach(({ element, size }) => {
      originalInlineStyles.set(element, {
        value: element.style.getPropertyValue('font-size'),
        priority: element.style.getPropertyPriority('font-size')
      });
      element.style.setProperty('font-size', formatPixels(size + INCREASE_PX), 'important');
      element.setAttribute(MARKER, 'true');
      adjustedElements.add(element);
    });
  };

  const restoreBaseline = () => {
    adjustedElements.forEach((element) => {
      if (!element.isConnected) return;

      const original = originalInlineStyles.get(element);
      if (original && original.value) {
        element.style.setProperty('font-size', original.value, original.priority);
      } else {
        element.style.removeProperty('font-size');
      }
      element.removeAttribute(MARKER);
    });
    adjustedElements.clear();
  };

  const scheduleUpdate = (recalibrate = false) => {
    recalibrateOnNextFrame = recalibrateOnNextFrame || recalibrate;
    if (frameId) return;

    frameId = window.requestAnimationFrame(() => {
      frameId = 0;
      if (recalibrateOnNextFrame) restoreBaseline();
      recalibrateOnNextFrame = false;
      applyIncrease();
    });
  };

  const initialise = () => {
    if (!document.querySelector(STEP_SELECTOR)) return;

    scheduleUpdate(true);

    const observer = new MutationObserver((mutations) => {
      const needsRecalibration = mutations.some((mutation) => mutation.type === 'attributes');
      scheduleUpdate(needsRecalibration);
    });

    document.querySelectorAll('.bcu-service-page').forEach((page) => {
      observer.observe(page, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'hidden', 'aria-hidden']
      });
    });

    let resizeTimer = 0;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => scheduleUpdate(true), 120);
    }, { passive: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
